import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaSyncAlt } from 'react-icons/fa';
import RTPageLayout from '../common/RTPageLayout';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import RTModal from '../common/RTModal';
import RTStatusPill, { formatStatusLabel } from '../common/statusPill';
import Table from '../common/Table';
import { apiGet, apiPost, apiPut, apiDelete } from '../../api/apiClient';
import { toastSuccess, toastError, toastConfirm, toastWarning } from '../common/toast';
import '../common/rtCommon.css';
import '../home/AdminDashboard.css';
import './WhatsAppTemplateManagement.css';

const CATEGORY_OPTIONS = [
  { value: 'UTILITY', label: 'Utility' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'AUTHENTICATION', label: 'Authentication' },
];

const EDITABLE_STATUSES = new Set(['APPROVED', 'REJECTED', 'PAUSED']);

const emptyForm = {
  name: '',
  language: 'en',
  category: 'UTILITY',
  body_named: '',
  header_text: '',
  footer_text: '',
  description: '',
  eligible_for_bulk_send: false,
};

const isAuthenticationTemplate = (tpl) =>
  String(tpl?.category || '').toUpperCase() === 'AUTHENTICATION';

const TemplateToggle = ({
  checked,
  disabled,
  onChange,
  onLabel,
  offLabel,
}) => (
  <label className={`wa-tpl-switch${disabled ? ' wa-tpl-switch-disabled' : ''}`}>
    <input
      type="checkbox"
      checked={!!checked}
      disabled={disabled}
      onChange={onChange}
    />
    <span className="wa-tpl-switch-slider" aria-hidden="true" />
    <span className="wa-tpl-switch-text">{checked ? onLabel : offLabel}</span>
  </label>
);

const getDisplayBody = (tpl) => {
  if (!tpl) return '';
  if (tpl.body_named) return tpl.body_named;
  if (tpl.body_meta && Array.isArray(tpl.param_tags) && tpl.param_tags.length) {
    return String(tpl.body_meta).replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n) => {
      const tag = tpl.param_tags[Number(n) - 1];
      return tag ? `{{${tag}}}` : `{{${n}}}`;
    });
  }
  return tpl.body_meta || '';
};

const WhatsAppTemplateManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [tags, setTags] = useState([]);
  const [syncWarning, setSyncWarning] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [viewTemplate, setViewTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const bodyRef = useRef(null);

  const [form, setForm] = useState(emptyForm);

  const isEditMode = !!editingTemplate;

  const loadTags = useCallback(async () => {
    try {
      const response = await apiGet('/api/whatsapp/message-template-tags');
      const data = response.data || response;
      setTags(Array.isArray(data.tags) ? data.tags.filter((t) => t.is_active) : []);
    } catch (err) {
      console.error(err);
      toastError('Failed to load template tags');
    }
  }, []);

  const syncTemplates = useCallback(async () => {
    try {
      setSyncWarning('');
      const response = await apiGet('/api/whatsapp/message-templates/sync');
      const data = response.data || response;
      setTemplates(Array.isArray(data.templates) ? data.templates : []);
      if (data.error) {
        setSyncWarning(data.error);
      }
    } catch (err) {
      console.error(err);
      toastError(err?.response?.data?.detail || 'Failed to sync templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    syncTemplates();
  }, [syncTemplates]);

  useEffect(() => {
    // Tags are only needed on create/edit — not on the templates list
    if (showEditor) {
      loadTags();
    }
  }, [showEditor, loadTags]);

  const resetEditor = () => {
    setShowEditor(false);
    setEditingTemplate(null);
    setForm(emptyForm);
  };

  const openCreate = () => {
    setEditingTemplate(null);
    setForm(emptyForm);
    setShowEditor(true);
    setViewTemplate(null);
  };

  const openEdit = (tpl) => {
    setEditingTemplate(tpl);
    setForm({
      name: tpl.name || '',
      language: tpl.language || 'en',
      category: tpl.category || 'UTILITY',
      body_named: getDisplayBody(tpl),
      header_text: tpl.header_text || '',
      footer_text: tpl.footer_text || '',
      description: tpl.description || '',
      eligible_for_bulk_send: !!tpl.eligible_for_bulk_send,
    });
    setShowEditor(true);
    setViewTemplate(null);
  };

  const canEditTemplate = (tpl) => {
    if (!tpl) return false;
    // AUTHENTICATION OTP body is managed by Meta
    if (isAuthenticationTemplate(tpl)) {
      return false;
    }
    if (!tpl.whatsapp_template_id) return false;
    const st = String(tpl.whatsapp_status || '').toUpperCase();
    return EDITABLE_STATUSES.has(st);
  };

  const headerActions = (
    <>
      <RTButton
        variant="secondary"
        onClick={() => navigate('/settings/whatsapp-settings')}
      >
        &#8592; Back
      </RTButton>
      {!showEditor ? (
        <RTButton
          variant="primary"
          onClick={() => navigate('/settings/whatsapp-send-template')}
        >
          Send Template Message
        </RTButton>
      ) : null}
      {!showEditor ? (
        <RTButton
          variant="primary"
          onClick={() => window.location.reload()}
          title="Refresh"
          className="refresh-button"
        >
          <span className="refresh-icon">
            <FaSyncAlt />
          </span>
          <span className="refresh-text">Refresh</span>
        </RTButton>
      ) : null}
      {!showEditor ? (
        <RTButton
          variant="primary"
          onClick={() => openCreate()}
        >
          Create Template
        </RTButton>
      ) : null}
    </>
  );

  const insertTag = (tagKey) => {
    const token = `{{${tagKey}}}`;
    const el = bodyRef.current;
    const current = form.body_named || '';
    if (el && typeof el.selectionStart === 'number') {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = current.slice(0, start) + token + current.slice(end);
      setForm((prev) => ({ ...prev, body_named: next }));
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      });
    } else {
      setForm((prev) => ({ ...prev, body_named: `${current}${token}` }));
    }
  };

  const handleToggleEnabled = async (row) => {
    if (toggling || deleting) return;
    const nextEnabled = !row.is_enabled;
    try {
      setToggling(true);
      const response = await apiPost(
        `/api/whatsapp/message-templates/${row.id}/enabled`,
        { is_enabled: nextEnabled }
      );
      const data = response.data || response;
      const updated = data.template;
      setTemplates((prev) =>
        prev.map((t) => (t.id === row.id ? { ...t, ...updated } : t))
      );
      toastSuccess(updated.is_enabled ? 'Template enabled' : 'Template disabled');
    } catch (err) {
      toastError(err?.response?.data?.detail || 'Failed to update template');
    } finally {
      setToggling(false);
    }
  };

  const handleToggleBulkSend = async (row) => {
    if (toggling || deleting) return;
    if (isAuthenticationTemplate(row)) {
      toastError('Authentication templates cannot be bulk-sent to allocations');
      return;
    }
    const nextEligible = !row.eligible_for_bulk_send;
    try {
      setToggling(true);
      const response = await apiPost(
        `/api/whatsapp/message-templates/${row.id}/eligible-for-bulk-send`,
        { eligible_for_bulk_send: nextEligible }
      );
      const data = response.data || response;
      const updated = data.template;
      setTemplates((prev) =>
        prev.map((t) => (t.id === row.id ? { ...t, ...updated } : t))
      );
      toastSuccess(
        updated.eligible_for_bulk_send
          ? 'Template marked eligible for bulk send'
          : 'Template removed from bulk send'
      );
    } catch (err) {
      toastError(err?.response?.data?.detail || 'Failed to update template');
    } finally {
      setToggling(false);
    }
  };

  const handleDelete = async (row) => {
    if (row.is_system) {
      toastError('System templates cannot be deleted');
      return;
    }
    const ok = await toastConfirm(
      true,
      `Delete template "${row.name}" from WhatsApp and the database?`
    );
    if (!ok) return;
    try {
      setDeleting(true);
      await apiDelete(`/api/whatsapp/message-templates/${row.id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== row.id));
      toastSuccess('Template deleted from WhatsApp');
    } catch (err) {
      toastError(err?.response?.data?.detail || 'Failed to delete template');
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (isEditMode) {
      const ok = await toastConfirm(
        true,
        `Update template "${editingTemplate?.name || form.name}" on WhatsApp? Approved templates can usually be edited only once per 24 hours.`
      );
      if (!ok) return;
    }
    try {
      setSaving(true);
      if (isEditMode) {
        const response = await apiPut(
          `/api/whatsapp/message-templates/${editingTemplate.id}`,
          {
            body_named: form.body_named,
            category: form.category,
            header_text: form.header_text || null,
            footer_text: form.footer_text || null,
            description: form.description || null,
          }
        );
        const data = response.data || response;
        const tpl = data.template;
        const status = String(tpl?.whatsapp_status || '').toUpperCase();
        if (status === 'REJECTED') {
          const reason =
            tpl?.rejected_reason_label || tpl?.rejected_reason || 'No reason provided';
          toastWarning(`Template updated but rejected by WhatsApp: ${reason}`);
        } else {
          toastSuccess('Template updated on WhatsApp');
        }
        if (tpl) {
          setTemplates((prev) =>
            prev.map((t) => (t.id === tpl.id ? tpl : t))
          );
        }
        resetEditor();
      } else {
        const response = await apiPost('/api/whatsapp/message-templates', {
          name: form.name.trim(),
          language: form.language.trim() || 'en',
          category: form.category,
          body_named: form.body_named,
          header_text: form.header_text || null,
          footer_text: form.footer_text || null,
          description: form.description || null,
          eligible_for_bulk_send: !!form.eligible_for_bulk_send,
        });
        const data = response.data || response;
        const tpl = data.template;
        const status = String(tpl?.whatsapp_status || '').toUpperCase();
        if (status === 'REJECTED') {
          const reason =
            tpl?.rejected_reason_label || tpl?.rejected_reason || 'No reason provided';
          toastWarning(`Template created but rejected by WhatsApp: ${reason}`);
        } else {
          toastSuccess('Template created on WhatsApp');
        }
        if (tpl) {
          setTemplates((prev) => {
            const without = prev.filter((t) => t.name !== tpl.name);
            return [tpl, ...without];
          });
        } else {
          await syncTemplates();
        }
        resetEditor();
      }
    } catch (err) {
      toastError(
        err?.response?.data?.detail ||
          (isEditMode ? 'Failed to update template' : 'Failed to create template')
      );
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
      Cell: ({ row }) => (
        <div>
          <div className="wa-tpl-name">{row.name}</div>
          {row.description ? (
            <div className="wa-tpl-desc">{row.description}</div>
          ) : null}
        </div>
      ),
    },
    {
      Header: 'Category',
      accessor: 'category',
      Cell: ({ value }) => value || '—',
    },
    {
      Header: 'Language',
      accessor: 'language',
    },
    {
      Header: 'WhatsApp Status',
      accessor: 'whatsapp_status',
      Cell: ({ row, value }) => (
        <div>
          <RTStatusPill status={value || 'unknown'}>
            {formatStatusLabel(value || 'unknown')}
          </RTStatusPill>
          {String(value || '').toUpperCase() === 'REJECTED' &&
          (row.rejected_reason_label || row.rejected_reason) ? (
            <div className="wa-tpl-reject-reason">
              {row.rejected_reason_label || row.rejected_reason}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      Header: 'Type',
      accessor: 'is_system',
      Cell: ({ row }) => (
        <RTStatusPill status={row.is_system ? 'info' : 'neutral'}>
          {row.is_system ? 'System' : row.is_custom ? 'Custom' : 'Imported'}
        </RTStatusPill>
      ),
    },
    {
      Header: 'Bulk Send',
      accessor: 'eligible_for_bulk_send',
      Cell: ({ row }) => {
        const authLocked = isAuthenticationTemplate(row);
        return (
          <TemplateToggle
            checked={!!row.eligible_for_bulk_send}
            disabled={toggling || deleting || authLocked}
            onChange={() => handleToggleBulkSend(row)}
            onLabel="Yes"
            offLabel="No"
          />
        );
      },
    },
    {
      Header: 'Enabled',
      accessor: 'is_enabled',
      Cell: ({ row }) => (
        <TemplateToggle
          checked={!!row.is_enabled}
          disabled={toggling || deleting}
          onChange={() => handleToggleEnabled(row)}
          onLabel="On"
          offLabel="Off"
        />
      ),
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      disableExport: true,
      Cell: ({ row }) => (
        <div className="wa-tpl-actions">
          <RTButton
            variant="secondary"
            size="small"
            onClick={() => setViewTemplate(row)}
          >
            View Message
          </RTButton>
          {canEditTemplate(row) ? (
            <RTButton
              variant="secondary"
              size="small"
              onClick={() => openEdit(row)}
            >
              Edit
            </RTButton>
          ) : null}
          <RTButton
            variant="danger"
            size="small"
            disabled={!!row.is_system || deleting}
            onClick={() => handleDelete(row)}
          >
            Delete
          </RTButton>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <RTPageLayout
        activePath={location.pathname}
        title="WhatsApp Templates"
        actions={headerActions}
        mainSection={true}
      >
        <div className="wa-tpl-spinner">
          <RTSpinner />
        </div>
      </RTPageLayout>
    );
  }

  return (
    <RTPageLayout
      activePath={location.pathname}
      title="WhatsApp Templates"
      actions={headerActions}
      mainSection={true}
    >
      <div className="wa-tpl-page">
        {deleting || toggling ? (
          <div className="wa-tpl-loading-overlay">
            <RTSpinner />
            <div className="wa-tpl-loading-text">
              {deleting ? 'Deleting template…' : 'Updating template…'}
            </div>
          </div>
        ) : null}
        {syncWarning ? (
          <div className="wa-tpl-warning">
            WhatsApp sync warning: {syncWarning}. Showing local catalog.
          </div>
        ) : null}

        {showEditor ? (
          <form className="wa-tpl-create" onSubmit={handleSave}>
            <h3 className="wa-tpl-create-title">
              {isEditMode
                ? editingTemplate?.is_system
                  ? 'Edit System Template'
                  : 'Edit Template'
                : 'Create Custom Template'}
            </h3>
            {isEditMode ? (
              <div className="wa-tpl-hint">
                Name and language cannot be changed on WhatsApp. Approved
                templates can be edited about once per day (max 10 times per 30
                days).
              </div>
            ) : null}
            <div className="wa-tpl-create-grid">
              <label>
                Name
                <input
                  className="form-control"
                  value={form.name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, name: e.target.value }))
                  }
                  placeholder="e.g. birthday_wishes"
                  required
                  disabled={isEditMode}
                />
              </label>
              <label>
                Language
                <input
                  className="form-control"
                  value={form.language}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, language: e.target.value }))
                  }
                  placeholder="en"
                  required
                  disabled={isEditMode}
                />
              </label>
              <label>
                Category
                <select
                  className="form-control"
                  value={form.category}
                  onChange={(e) => {
                    const category = e.target.value;
                    setForm((p) => ({
                      ...p,
                      category,
                      eligible_for_bulk_send:
                        category === 'AUTHENTICATION'
                          ? false
                          : p.eligible_for_bulk_send,
                    }));
                  }}
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Description (optional)
                <input
                  className="form-control"
                  value={form.description}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, description: e.target.value }))
                  }
                />
              </label>
              {!isEditMode ? (
                <label>
                  Eligible for bulk send
                  <select
                    className="form-control"
                    value={form.eligible_for_bulk_send ? 'yes' : 'no'}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        eligible_for_bulk_send: e.target.value === 'yes',
                      }))
                    }
                    disabled={form.category === 'AUTHENTICATION'}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </label>
              ) : null}
            </div>
            {!isEditMode ? (
              <div className="wa-tpl-hint">
                Only templates marked Yes appear when sending a template message
                to allocations.
              </div>
            ) : null}

            <label className="wa-tpl-full">
              Header (optional)
              <input
                className="form-control"
                value={form.header_text}
                onChange={(e) =>
                  setForm((p) => ({ ...p, header_text: e.target.value }))
                }
              />
            </label>

            <div className="wa-tpl-full">
              <div className="wa-tpl-body-label">Body</div>
              <div className="wa-tpl-tags-header">
                <div className="wa-tpl-tags-label">Insert tag</div>
                <button
                  type="button"
                  className="wa-tpl-tags-page-link"
                  onClick={() => navigate('/settings/whatsapp-template-tags')}
                >
                  View all tags
                </button>
              </div>
              <div className="wa-tpl-tags">
                {tags.length === 0 ? (
                  <span className="wa-tpl-hint">Loading tags…</span>
                ) : (
                  tags.map((tag) => (
                    <button
                      key={tag.tag_key}
                      type="button"
                      className="wa-tpl-tag-chip"
                      onClick={() => insertTag(tag.tag_key)}
                      title={
                        tag.example_value
                          ? `${tag.description || tag.display_label} · Example: ${tag.example_value}`
                          : tag.description || tag.display_label
                      }
                    >
                      <span className="wa-tpl-tag-chip-label">
                        {tag.display_label}
                      </span>
                      <span className="wa-tpl-tag-chip-key">{`{{${tag.tag_key}}}`}</span>
                    </button>
                  ))
                )}
              </div>
              <textarea
                ref={bodyRef}
                className="form-control wa-tpl-body"
                rows={5}
                value={form.body_named}
                onChange={(e) =>
                  setForm((p) => ({ ...p, body_named: e.target.value }))
                }
                placeholder="Hello {{pgName}}, ..."
                required
              />
              <div className="wa-tpl-hint">
                Click a tag above to insert it. Only these predefined tags are
                allowed inside {'{{}}'}.
              </div>
            </div>

            <label className="wa-tpl-full">
              Footer (optional)
              <input
                className="form-control"
                value={form.footer_text}
                onChange={(e) =>
                  setForm((p) => ({ ...p, footer_text: e.target.value }))
                }
              />
            </label>

            <div className="wa-tpl-create-actions">
              <RTButton
                type="button"
                variant="secondary"
                onClick={resetEditor}
                disabled={saving}
              >
                Cancel
              </RTButton>
              <RTButton type="submit" variant="primary" disabled={saving}>
                {saving
                  ? isEditMode
                    ? 'Updating…'
                    : 'Creating…'
                  : isEditMode
                    ? 'Update on WhatsApp'
                    : 'Create on WhatsApp'}
              </RTButton>
            </div>
          </form>
        ) : (
          <Table
            columns={columns}
            data={templates}
            showSearch={false}
            exportable={false}
          />
        )}
      </div>

      <RTModal
        isOpen={!!viewTemplate}
        onClose={() => setViewTemplate(null)}
        title={viewTemplate ? `Template: ${viewTemplate.name}` : 'Template'}
        size="large"
        footer={
          <>
            {canEditTemplate(viewTemplate) ? (
              <RTButton
                variant="primary"
                onClick={() => openEdit(viewTemplate)}
              >
                Edit Template
              </RTButton>
            ) : null}
            <RTButton
              variant="secondary"
              onClick={() => setViewTemplate(null)}
            >
              Close
            </RTButton>
          </>
        }
      >
        {viewTemplate ? (
          <div className="wa-tpl-view">
            <div className="wa-tpl-view-meta">
              <span>
                Status:{' '}
                <RTStatusPill status={viewTemplate.whatsapp_status || 'unknown'}>
                  {formatStatusLabel(viewTemplate.whatsapp_status || 'unknown')}
                </RTStatusPill>
              </span>
              {String(viewTemplate.whatsapp_status || '').toUpperCase() ===
                'REJECTED' &&
              (viewTemplate.rejected_reason_label ||
                viewTemplate.rejected_reason) ? (
                <span className="wa-tpl-reject-reason">
                  Reason:{' '}
                  {viewTemplate.rejected_reason_label ||
                    viewTemplate.rejected_reason}
                </span>
              ) : null}
              <span>
                Type:{' '}
                {viewTemplate.is_system
                  ? 'System'
                  : viewTemplate.is_custom
                    ? 'Custom'
                    : 'Imported'}
              </span>
              <span>Language: {viewTemplate.language || '—'}</span>
              <span>Category: {viewTemplate.category || '—'}</span>
              <span>
                Eligible for bulk send:{' '}
                {viewTemplate.eligible_for_bulk_send ? 'Yes' : 'No'}
              </span>
            </div>
            {viewTemplate.header_text ? (
              <div className="wa-tpl-view-block">
                <div className="wa-tpl-view-label">Header</div>
                <div className="wa-tpl-view-text">{viewTemplate.header_text}</div>
              </div>
            ) : null}
            <div className="wa-tpl-view-block">
              <div className="wa-tpl-view-label">Body</div>
              <div className="wa-tpl-view-text">
                {getDisplayBody(viewTemplate) || 'No message body available.'}
              </div>
            </div>
            {viewTemplate.footer_text ? (
              <div className="wa-tpl-view-block">
                <div className="wa-tpl-view-label">Footer</div>
                <div className="wa-tpl-view-text">{viewTemplate.footer_text}</div>
              </div>
            ) : null}
            {viewTemplate.is_custom && !canEditTemplate(viewTemplate) ? (
              <div className="wa-tpl-hint">
                This custom template cannot be edited right now
                {String(viewTemplate.whatsapp_status || '').toUpperCase() ===
                'PENDING'
                  ? ' because it is still pending WhatsApp approval.'
                  : '.'}
              </div>
            ) : null}
            {viewTemplate.is_system &&
            String(viewTemplate.category || '').toUpperCase() ===
              'AUTHENTICATION' ? (
              <div className="wa-tpl-hint">
                Authentication (OTP) templates cannot be edited here — Meta
                manages the message body.
              </div>
            ) : null}
            {viewTemplate.is_system &&
            String(viewTemplate.category || '').toUpperCase() !==
              'AUTHENTICATION' &&
            !canEditTemplate(viewTemplate) ? (
              <div className="wa-tpl-hint">
                This system template cannot be edited right now
                {!viewTemplate.whatsapp_template_id
                  ? ' because it is not on WhatsApp yet. Use Refresh to sync/create it.'
                  : String(viewTemplate.whatsapp_status || '').toUpperCase() ===
                      'PENDING'
                    ? ' because it is still pending WhatsApp approval.'
                    : '.'}
              </div>
            ) : null}
          </div>
        ) : null}
      </RTModal>
    </RTPageLayout>
  );
};

export default WhatsAppTemplateManagement;
